import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  FlexBox,
  Text,
  Label,
  Input,
  Button,
  BusyIndicator,
  Icon,
  Title,
} from '@ui5/webcomponents-react';
import { askGuestChatbot } from './api';

const SUGGESTIONS = [
  "What's the WiFi password?",
  'What time is check-in?',
  'Are there house rules?',
  'Any local food recommendations?',
];

function friendlyError(code) {
  switch (code) {
    case 'rate_limit_hour':    return 'Too many questions right now — please try again later.';
    case 'rate_limit_day':     return 'Daily chat limit reached — please try again tomorrow.';
    case 'rate_limit_ip':      return 'Too many questions from this device — please try again later.';
    case 'chatbot_disabled':   return 'The host has not enabled the assistant.';
    case 'location_not_found': return "Couldn't find this property.";
    default:                   return 'Something went wrong — please try again.';
  }
}

function GuestChatbot({ uuid, onNavigate }) {
  const [open, setOpen]         = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput]       = useState('');
  const [busy, setBusy]         = useState(false);
  const [convId, setConvId]     = useState(null);
  const [error, setError]       = useState('');
  const scrollRef = useRef(null);

  useEffect(() => {
    if (open && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, busy, open]);

  const send = useCallback(async (text) => {
    const msg = (typeof text === 'string' ? text : input).trim();
    if (!msg || busy) return;

    // Snapshot history BEFORE adding the new turn — server expects prior turns only.
    const history = messages.slice(-16).map(({ role, content }) => ({ role, content }));

    setMessages(prev => [...prev, { role: 'user', content: msg }]);
    setInput('');
    setBusy(true);
    setError('');

    try {
      const res = await askGuestChatbot(uuid, msg, convId, history);
      if (res && res.error) {
        setError(res.message || friendlyError(res.error));
      } else if (res && res.reply) {
        if (res.conversationId) setConvId(res.conversationId);
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: res.reply,
          messageId: res.messageId || null,
        }]);
        if (res.navigate && typeof onNavigate === 'function') {
          // Brief delay so the user sees the reply before the screen changes.
          setTimeout(() => onNavigate(res.navigate), 900);
        }
      } else {
        setError('No reply — please try again.');
      }
    } catch (e) {
      setError('Network error — please try again.');
    } finally {
      setBusy(false);
    }
  }, [input, busy, messages, uuid, convId, onNavigate]);

  return (
    <>
      <button
        type="button"
        className="chatbot-fab"
        data-open={open ? 'true' : 'false'}
        onClick={() => setOpen(o => !o)}
        aria-label={open ? 'Close assistant' : 'Open assistant'}
      >
        <Icon name={open ? 'decline' : 'ai'} />
      </button>

      {open && (
        <div className="chatbot-sheet" role="dialog" aria-label="Property assistant">
          <div className="chatbot-sheet-header">
            <FlexBox alignItems="Center" style={{ gap: '0.5rem' }}>
              <Icon name="ai" />
              <Title level="H5" style={{ margin: 0 }}>Property Assistant</Title>
            </FlexBox>
          </div>

          <div className="chatbot-scroll" ref={scrollRef}>
            {messages.length === 0 && (
              <FlexBox direction="Column" style={{ gap: '0.5rem' }}>
                <Label>How can I help?</Label>
                <Text>Ask about WiFi, check-in, house rules, devices, or local tips.</Text>
                <FlexBox wrap="Wrap" style={{ gap: '0.4rem', marginTop: '0.25rem' }}>
                  {SUGGESTIONS.map((s) => (
                    <Button key={s} design="Default" onClick={() => send(s)} disabled={busy}>
                      {s}
                    </Button>
                  ))}
                </FlexBox>
              </FlexBox>
            )}

            {messages.map((m, i) => (
              <div key={i} className={`chat-bubble chat-${m.role}`}>
                <div style={{ whiteSpace: 'pre-wrap' }}>{m.content}</div>
              </div>
            ))}

            {busy && (
              <div className="chat-bubble chat-assistant chat-thinking">
                <BusyIndicator active size="S" />
              </div>
            )}
          </div>

          {error && <div className="chat-error">{error}</div>}

          <FlexBox alignItems="Center" className="chat-input-row" style={{ gap: '0.5rem' }}>
            <Input
              value={input}
              placeholder="Type a question..."
              disabled={busy}
              onInput={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') send(); }}
              style={{ flex: 1 }}
            />
            <Button
              design="Emphasized"
              icon="paper-plane"
              onClick={() => send()}
              disabled={busy || !input.trim()}
              aria-label="Send"
            />
          </FlexBox>
        </div>
      )}
    </>
  );
}

export default GuestChatbot;
